import {
    StrictEventEmitter,
    EventMap,
    EventListenerFn,
    EventAllListenerFn,
    EventOneOfListenerFn,
    FunctionArgsTernary,
    EventArg
} from './StrictEventEmitter';

export type StrictEventEmitterWrapperOptions = {
    signal?: AbortSignal;
    owner?: object;
};

export class SignalEventEmitterWrapper<T extends EventMap> {
    constructor(
        readonly emitter: StrictEventEmitter<T>,
        readonly options: StrictEventEmitterWrapperOptions
    ) {}

    on<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): () => void {
        return this.emitter.on(eventName, cb, this.options);
    }

    once<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): () => void {
        return this.emitter.once(eventName, cb, this.options);
    }

    onceAll<K extends keyof T, P extends K[]>(eventNames: P, cb: EventAllListenerFn<T, P[number]>): () => void {
        return this.emitter.onceAll(eventNames, cb, this.options);
    }

    onceOneOf<K extends keyof T, P extends K[]>(eventNames: P, cb: EventOneOfListenerFn<T, P[number]>): () => void {
        return this.emitter.onceOneOf(eventNames, cb, this.options);
    }

    emit<K extends keyof T, V extends T[K]>(...args: FunctionArgsTernary<V, [key: K, val: V], [key: K]>): void {
        return this.emitter.emit(...args);
    }

    onceAsync<K extends keyof T>(eventName: K): Promise<EventArg<T, K>> {
        return this.emitter.onceAsync(eventName, this.options);
    }

    onceAllAsync<K extends keyof T, P extends K[]>(eventNames: P): Promise<{ [P in K]: EventArg<T, P> }> {
        return this.emitter.onceAllAsync(eventNames, this.options);
    }

    onceOneOfAsync<K extends keyof T, P extends K[]>(eventNames: P): Promise<{ [P in K]?: EventArg<T, P> }> {
        return this.emitter.onceOneOfAsync(eventNames, this.options);
    }

    async emitAsync<K extends keyof T, V extends T[K]>(
        ...args: FunctionArgsTernary<V, [key: K, val: V], [key: K, val?: undefined]>
    ): Promise<void> {
        return this.emitter.emitAsync(...args);
    }

    off<K extends keyof T>(eventName: K, cb: EventListenerFn<T, K>): void {
        return this.emitter.off(eventName, cb);
    }

    offByOwner<K extends keyof T>(eventName: K): void {
        const {owner} = this.options;

        if (!owner) {
            throw new Error('owner is missing');
        }

        return this.emitter.offByOwner(eventName, owner);
    }

    offAllByOwner(): void {
        const {owner} = this.options;

        if (!owner) {
            throw new Error('owner is missing');
        }

        return this.emitter.offAllByOwner(owner);
    }
}
